<!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
<script setup>
/* eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text */
import { computed, ref, watch } from 'vue';
import LaisCard from 'dashboard/components-next/LaisCard.vue';
import LaisPrimaryButton from 'dashboard/components-next/LaisPrimaryButton.vue';
import PropertyDiligenceCasesAPI from 'dashboard/api/propertyDiligenceCases';
import { useAlert } from 'dashboard/composables';

const props = defineProps({
  imovelId: { type: [Number, String], default: '' },
  imovelListingId: { type: [Number, String], default: '' },
});

const emit = defineEmits(['updated']);

const cases = ref([]);
const operationalStatus = ref(null);
const promotionGate = ref(null);
const loading = ref(false);
const statusLoading = ref(false);
const promotionGateLoading = ref(false);
const saving = ref(false);
const error = ref(false);
const statusError = ref(false);
const promotionGateError = ref(false);
const evidenceProtocol = ref('');
const evidenceKind = ref('matricula');
const sourceType = ref('onr');
const checklistItemKey = ref('matricula_atualizada');
const sourceAttemptReason = ref('provider_not_configured');
const reviewReason = ref('');

const activeCase = computed(() => cases.value[0] || null);
const checklistItems = computed(() => activeCase.value?.checklist_items || []);
const evidences = computed(() => activeCase.value?.evidences || []);
const openBlockerCount = computed(
  () => activeCase.value?.summary_snapshot?.open_blocker_count || 0
);
const statusAggregate = computed(
  () => operationalStatus.value?.aggregate || {}
);
const caseSummaries = computed(
  () => operationalStatus.value?.case_summaries || []
);
const providerMode = computed(
  () => operationalStatus.value?.provider_mode || {}
);
const promotionDecision = computed(() => promotionGate.value?.promotion || {});
const promotionGates = computed(() => promotionGate.value?.gates || {});
const promotionSources = computed(
  () => promotionGate.value?.source_feeding?.sources || []
);
const promotionGateCards = computed(() => [
  {
    key: 'external_evidence_complete',
    label: 'Evidência externa',
    gate: promotionGates.value.external_evidence_complete,
  },
  {
    key: 'human_review_complete',
    label: 'Revisão humana',
    gate: promotionGates.value.human_review_complete,
  },
  {
    key: 'backtest_attached',
    label: 'Backtest',
    gate: promotionGates.value.backtest_attached,
  },
  {
    key: 'redaction_enforced',
    label: 'Redaction',
    gate: promotionGates.value.redaction_enforced,
  },
]);
const sourceAttemptSummaries = computed(() => {
  if (!activeCase.value) return [];
  const summary = caseSummaries.value.find(
    item => item.id === activeCase.value.id
  );
  return summary?.source_attempts || [];
});
const firstOpenItem = computed(
  () =>
    checklistItems.value.find(
      item => item.blocker && item.required && !item.cleared
    ) || null
);
const firstEvidence = computed(() => evidences.value[0] || null);
const canCreate = computed(() => Boolean(props.imovelId) && !saving.value);

const formatDate = value => {
  if (!value) return 'Sem data';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return new Intl.DateTimeFormat('pt-BR', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  }).format(date);
};

const loadCases = async () => {
  if (!props.imovelId) {
    cases.value = [];
    return;
  }

  loading.value = true;
  error.value = false;
  try {
    const { data } = await PropertyDiligenceCasesAPI.get({
      imovel_id: props.imovelId,
      limit: 5,
    });
    cases.value = data.data || [];
  } catch {
    error.value = true;
    cases.value = [];
  } finally {
    loading.value = false;
  }
};

const loadOperationalStatus = async () => {
  if (!props.imovelId) {
    operationalStatus.value = null;
    return;
  }

  statusLoading.value = true;
  statusError.value = false;
  try {
    const { data } = await PropertyDiligenceCasesAPI.operationalStatus({
      imovel_id: props.imovelId,
      limit: 10,
    });
    operationalStatus.value = data;
  } catch {
    statusError.value = true;
    operationalStatus.value = null;
  } finally {
    statusLoading.value = false;
  }
};

const loadPromotionGate = async () => {
  if (!props.imovelId) {
    promotionGate.value = null;
    return;
  }

  promotionGateLoading.value = true;
  promotionGateError.value = false;
  try {
    const { data } = await PropertyDiligenceCasesAPI.promotionGate({
      imovel_id: props.imovelId,
      limit: 10,
    });
    promotionGate.value = data;
  } catch {
    promotionGateError.value = true;
    promotionGate.value = null;
  } finally {
    promotionGateLoading.value = false;
  }
};

const loadPanel = () => {
  loadCases();
  loadOperationalStatus();
  loadPromotionGate();
};

const createCase = async () => {
  if (!canCreate.value) return;

  saving.value = true;
  try {
    const { data } = await PropertyDiligenceCasesAPI.create({
      imovel_id: props.imovelId,
      imovel_listing_id: props.imovelListingId,
      purpose: 'sale',
    });
    cases.value = [data, ...cases.value.filter(item => item.id !== data.id)];
    useAlert('Caso de diligência criado.');
    emit('updated', data);
    loadOperationalStatus();
    loadPromotionGate();
  } catch {
    useAlert('Não foi possível criar a diligência.');
  } finally {
    saving.value = false;
  }
};

const collectRegistryIdentity = async () => {
  if (!activeCase.value || saving.value) return;
  saving.value = true;
  try {
    const { data } = await PropertyDiligenceCasesAPI.collectRegistryIdentity(
      activeCase.value.id,
      { source_type: 'onr' }
    );
    cases.value = [data, ...cases.value.filter(item => item.id !== data.id)];
    useAlert('Tentativa registral registrada.');
    emit('updated', data);
    loadOperationalStatus();
    loadPromotionGate();
  } catch {
    useAlert('Não foi possível registrar a tentativa registral.');
  } finally {
    saving.value = false;
  }
};

const recordSourceAttempt = async () => {
  if (!activeCase.value || saving.value) return;
  saving.value = true;
  try {
    const { data } = await PropertyDiligenceCasesAPI.recordSourceAttempt(
      activeCase.value.id,
      {
        source_type: sourceType.value,
        checklist_item_key: checklistItemKey.value,
        reason_code: sourceAttemptReason.value,
        message: `${sourceAttemptReason.value} via diligence_review_panel`,
      }
    );
    cases.value = [data, ...cases.value.filter(item => item.id !== data.id)];
    useAlert('Tentativa local registrada sem provider externo.');
    emit('updated', data);
    loadOperationalStatus();
    loadPromotionGate();
  } catch {
    useAlert('Não foi possível registrar a tentativa local.');
  } finally {
    saving.value = false;
  }
};

const addEvidence = async () => {
  if (!activeCase.value || saving.value) return;
  saving.value = true;
  try {
    const { data } = await PropertyDiligenceCasesAPI.addEvidence(
      activeCase.value.id,
      {
        evidence_kind: evidenceKind.value,
        source_type: sourceType.value,
        protocol: evidenceProtocol.value,
        checklist_item_key: checklistItemKey.value,
        payload: {
          protocol: evidenceProtocol.value,
          surface: 'diligence_review_panel',
        },
        valid_until: new Date(
          Date.now() + 30 * 24 * 60 * 60 * 1000
        ).toISOString(),
      }
    );
    cases.value = [data, ...cases.value.filter(item => item.id !== data.id)];
    evidenceProtocol.value = '';
    useAlert('Evidência registrada.');
    emit('updated', data);
    loadOperationalStatus();
    loadPromotionGate();
  } catch {
    useAlert('Não foi possível registrar a evidência.');
  } finally {
    saving.value = false;
  }
};

const reviewOpenItem = async decision => {
  if (!activeCase.value || !firstOpenItem.value || saving.value) return;
  saving.value = true;
  try {
    const { data } = await PropertyDiligenceCasesAPI.reviewItem(
      activeCase.value.id,
      {
        checklist_item_id: firstOpenItem.value.id,
        property_evidence_id: firstEvidence.value?.id,
        decision,
        decision_reason: reviewReason.value || 'revisao humana registrada',
      }
    );
    cases.value = [data, ...cases.value.filter(item => item.id !== data.id)];
    reviewReason.value = '';
    useAlert('Revisão de diligência registrada.');
    emit('updated', data);
    loadOperationalStatus();
    loadPromotionGate();
  } catch {
    useAlert('Não foi possível revisar o item.');
  } finally {
    saving.value = false;
  }
};

watch(() => [props.imovelId, props.imovelListingId], loadPanel, {
  immediate: true,
});
</script>

<template>
  <LaisCard title="Diligência registral">
    <div class="grid gap-4">
      <div
        v-if="loading"
        class="rounded-lais border border-lais-border-soft bg-lais-bg-muted px-4 py-6 text-[13px] text-lais-text-muted"
      >
        Carregando diligência...
      </div>

      <div
        v-else-if="error"
        class="rounded-lais border border-rose-100 bg-rose-50 px-4 py-3 text-[13px] text-rose-800"
      >
        Diligência indisponível no momento.
      </div>

      <div
        v-else-if="!activeCase"
        class="grid gap-3 rounded-lais border border-lais-border-soft bg-lais-bg-muted px-4 py-4 text-[13px]"
      >
        <p class="font-semibold text-lais-text">Nenhum caso registral ativo.</p>
        <p class="text-lais-text-muted">
          O caso preserva evidências redigidas, validade, checklist e revisão
          humana.
        </p>
        <div>
          <LaisPrimaryButton
            icon="i-lucide-shield-plus"
            :disabled="!canCreate"
            :is-loading="saving"
            @click="createCase"
          >
            Criar diligência
          </LaisPrimaryButton>
        </div>
      </div>

      <template v-else>
        <div class="grid gap-3 sm:grid-cols-4">
          <div class="rounded-lais-sm border border-lais-border-soft px-3 py-2">
            <p class="text-[11px] font-semibold uppercase text-lais-text-muted">
              Status
            </p>
            <p class="mt-1 text-[13px] font-semibold text-lais-text">
              {{ activeCase.status }}
            </p>
          </div>
          <div class="rounded-lais-sm border border-lais-border-soft px-3 py-2">
            <p class="text-[11px] font-semibold uppercase text-lais-text-muted">
              Blockers
            </p>
            <p class="mt-1 text-[13px] font-semibold text-lais-text">
              {{ openBlockerCount }}
            </p>
          </div>
          <div class="rounded-lais-sm border border-lais-border-soft px-3 py-2">
            <p class="text-[11px] font-semibold uppercase text-lais-text-muted">
              Evidências
            </p>
            <p class="mt-1 text-[13px] font-semibold text-lais-text">
              {{ evidences.length }}
            </p>
          </div>
          <div class="rounded-lais-sm border border-lais-border-soft px-3 py-2">
            <p class="text-[11px] font-semibold uppercase text-lais-text-muted">
              Versão
            </p>
            <p class="mt-1 text-[13px] font-semibold text-lais-text">
              {{ activeCase.checklist_version }}
            </p>
          </div>
        </div>

        <div
          v-if="statusLoading"
          class="rounded-lais border border-lais-border-soft bg-lais-bg-muted px-4 py-3 text-[13px] text-lais-text-muted"
        >
          Carregando status operacional...
        </div>

        <div
          v-else-if="statusError"
          class="rounded-lais border border-amber-100 bg-amber-50 px-4 py-3 text-[13px] text-amber-900"
        >
          Status operacional indisponível.
        </div>

        <div
          v-else-if="operationalStatus"
          class="grid gap-3 rounded-lais border border-lais-border-soft bg-white px-4 py-4"
        >
          <div class="flex flex-wrap items-start justify-between gap-3">
            <div>
              <p class="text-[13px] font-semibold text-lais-text">
                Observabilidade local redigida
              </p>
              <p class="mt-1 text-[12px] text-lais-text-muted">
                Provider externo:
                {{
                  providerMode.external_provider_called ? 'ativo' : 'desativado'
                }}
                · Payload: {{ providerMode.payload_redaction }}
              </p>
            </div>
            <span
              class="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2.5 py-1 text-[11px] font-semibold text-emerald-800"
            >
              <span class="i-lucide-eye-off size-3.5" aria-hidden="true" />
              Sem payload bruto
            </span>
          </div>

          <div class="grid gap-2 sm:grid-cols-4">
            <div
              class="rounded-lais-sm border border-lais-border-soft px-3 py-2"
            >
              <p
                class="text-[11px] font-semibold uppercase text-lais-text-muted"
              >
                Casos
              </p>
              <p class="mt-1 text-[13px] font-semibold text-lais-text">
                {{ statusAggregate.case_count || 0 }}
              </p>
            </div>
            <div
              class="rounded-lais-sm border border-lais-border-soft px-3 py-2"
            >
              <p
                class="text-[11px] font-semibold uppercase text-lais-text-muted"
              >
                Blockers
              </p>
              <p class="mt-1 text-[13px] font-semibold text-lais-text">
                {{ statusAggregate.open_blocker_count || 0 }}
              </p>
            </div>
            <div
              class="rounded-lais-sm border border-lais-border-soft px-3 py-2"
            >
              <p
                class="text-[11px] font-semibold uppercase text-lais-text-muted"
              >
                Evidências
              </p>
              <p class="mt-1 text-[13px] font-semibold text-lais-text">
                {{ statusAggregate.evidence_count || 0 }}
              </p>
            </div>
            <div
              class="rounded-lais-sm border border-lais-border-soft px-3 py-2"
            >
              <p
                class="text-[11px] font-semibold uppercase text-lais-text-muted"
              >
                A vencer
              </p>
              <p class="mt-1 text-[13px] font-semibold text-lais-text">
                {{ statusAggregate.evidence_expiring_soon_count || 0 }}
              </p>
            </div>
          </div>

          <div v-if="caseSummaries.length" class="grid gap-2">
            <div
              v-for="summary in caseSummaries.slice(0, 3)"
              :key="summary.id"
              class="flex flex-wrap items-center justify-between gap-3 border-t border-lais-border-soft pt-2 text-[12px]"
            >
              <span class="min-w-0 text-lais-text-muted">
                {{ summary.case_number }} · {{ summary.status }} ·
                {{ formatDate(summary.latest_evidence_at) }}
              </span>
              <strong class="text-lais-text">
                {{ summary.open_blocker_count || 0 }} blockers
              </strong>
            </div>
          </div>

          <div
            v-if="sourceAttemptSummaries.length"
            class="grid gap-2 border-t border-lais-border-soft pt-2"
          >
            <p class="text-[12px] font-semibold text-lais-text">
              Tentativas de fonte
            </p>
            <div
              v-for="attempt in sourceAttemptSummaries"
              :key="`${attempt.source_type}-${attempt.collected_at}`"
              class="flex flex-wrap items-center justify-between gap-2 text-[12px]"
            >
              <span class="text-lais-text-muted">
                {{ attempt.source_type }} ·
                {{ attempt.reason_code || attempt.status }} ·
                {{ formatDate(attempt.collected_at) }}
              </span>
              <span class="font-semibold text-amber-800">
                {{ attempt.fail_closed ? 'fail-closed' : 'registrada' }}
              </span>
            </div>
          </div>
        </div>

        <div
          v-if="promotionGateLoading"
          class="rounded-lais border border-lais-border-soft bg-lais-bg-muted px-4 py-3 text-[13px] text-lais-text-muted"
        >
          Carregando gate de promoção...
        </div>

        <div
          v-else-if="promotionGateError"
          class="rounded-lais border border-amber-100 bg-amber-50 px-4 py-3 text-[13px] text-amber-900"
        >
          Gate de promoção indisponível.
        </div>

        <div
          v-else-if="promotionGate"
          class="grid gap-3 rounded-lais border border-lais-border-soft bg-white px-4 py-4"
        >
          <div class="flex flex-wrap items-start justify-between gap-3">
            <div>
              <p class="text-[13px] font-semibold text-lais-text">
                Gate de promoção local
              </p>
              <p class="mt-1 text-[12px] text-lais-text-muted">
                {{
                  promotionDecision.required_external_evidence
                    ? 'Exige evidência externa real'
                    : 'Evidência externa opcional'
                }}
                · Provider:
                {{
                  promotionGate.provider_calls_performed
                    ? 'executado'
                    : 'não executado'
                }}
              </p>
            </div>
            <span
              class="inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-semibold"
              :class="
                promotionDecision.allowed
                  ? 'bg-emerald-50 text-emerald-800'
                  : 'bg-amber-50 text-amber-800'
              "
            >
              <span
                :class="
                  promotionDecision.allowed
                    ? 'i-lucide-check-circle-2'
                    : 'i-lucide-lock'
                "
                class="size-3.5"
                aria-hidden="true"
              />
              {{ promotionDecision.allowed ? 'liberado' : 'bloqueado' }}
            </span>
          </div>

          <div class="grid gap-2 sm:grid-cols-4">
            <div
              v-for="card in promotionGateCards"
              :key="card.key"
              class="rounded-lais-sm border border-lais-border-soft px-3 py-2"
            >
              <p
                class="text-[11px] font-semibold uppercase text-lais-text-muted"
              >
                {{ card.label }}
              </p>
              <p
                class="mt-1 text-[13px] font-semibold"
                :class="
                  card.gate?.passed ? 'text-emerald-700' : 'text-amber-800'
                "
              >
                {{ card.gate?.passed ? 'ok' : 'pendente' }}
              </p>
            </div>
          </div>

          <div v-if="promotionSources.length" class="grid gap-2">
            <div
              v-for="source in promotionSources"
              :key="source.source_type"
              class="flex flex-wrap items-center justify-between gap-2 border-t border-lais-border-soft pt-2 text-[12px]"
            >
              <span class="text-lais-text-muted">
                {{ source.source_type }} · {{ source.status }}
              </span>
              <strong class="text-lais-text">
                {{ source.external_evidence_count || 0 }} externas
              </strong>
            </div>
          </div>
        </div>

        <div class="grid gap-2">
          <div
            v-for="item in checklistItems"
            :key="item.id"
            class="flex flex-wrap items-center justify-between gap-3 rounded-lais border border-lais-border-soft px-3 py-2 text-[13px]"
          >
            <span class="min-w-0">
              <strong class="block text-lais-text">{{ item.item_key }}</strong>
              <span class="text-lais-text-muted">
                {{ item.status }} ·
                {{ item.required ? 'obrigatório' : 'opcional' }}
              </span>
            </span>
            <span
              class="rounded-full px-2 py-1 text-[11px] font-semibold"
              :class="
                item.cleared
                  ? 'bg-emerald-50 text-emerald-700'
                  : 'bg-amber-50 text-amber-800'
              "
            >
              {{ item.cleared ? 'liberado' : 'pendente' }}
            </span>
          </div>
        </div>

        <div
          class="grid gap-3 rounded-lais border border-lais-border-soft px-4 py-4"
        >
          <div class="flex flex-wrap justify-between gap-3">
            <LaisPrimaryButton
              variant="ghost"
              icon="i-lucide-landmark"
              :is-loading="saving"
              @click="collectRegistryIdentity"
            >
              Consultar identidade
            </LaisPrimaryButton>
            <LaisPrimaryButton
              variant="ghost"
              icon="i-lucide-file-warning"
              :is-loading="saving"
              @click="recordSourceAttempt"
            >
              Registrar tentativa local
            </LaisPrimaryButton>
          </div>
          <div class="grid gap-3 sm:grid-cols-3">
            <label
              class="grid gap-1 text-[12px] font-semibold text-lais-text-muted"
            >
              Evidência
              <select
                v-model="evidenceKind"
                class="h-10 rounded-lais border border-lais-border px-3 text-[14px] font-normal text-lais-text"
              >
                <option value="matricula">Matrícula</option>
                <option value="iptu">IPTU</option>
                <option value="certidao_onus">Ônus</option>
                <option value="property_identity">Identidade</option>
              </select>
            </label>
            <label
              class="grid gap-1 text-[12px] font-semibold text-lais-text-muted"
            >
              Fonte
              <select
                v-model="sourceType"
                class="h-10 rounded-lais border border-lais-border px-3 text-[14px] font-normal text-lais-text"
              >
                <option value="onr">ONR</option>
                <option value="pgeo3">PGeo3</option>
                <option value="iptu">IPTU</option>
                <option value="manual">Manual</option>
              </select>
            </label>
            <label
              class="grid gap-1 text-[12px] font-semibold text-lais-text-muted"
            >
              Protocolo
              <input
                v-model="evidenceProtocol"
                class="h-10 rounded-lais border border-lais-border px-3 text-[14px] font-normal text-lais-text"
              />
            </label>
          </div>
          <label
            class="grid gap-1 text-[12px] font-semibold text-lais-text-muted"
          >
            Motivo da tentativa local
            <select
              v-model="sourceAttemptReason"
              class="h-10 rounded-lais border border-lais-border px-3 text-[14px] font-normal text-lais-text"
            >
              <option value="provider_not_configured">
                Provider não configurado
              </option>
              <option value="legal_gate_pending">
                Liberação jurídica pendente
              </option>
              <option value="pilot_not_enabled">Piloto não habilitado</option>
              <option value="source_timeout">Timeout local controlado</option>
            </select>
          </label>
          <label
            class="grid gap-1 text-[12px] font-semibold text-lais-text-muted"
          >
            Item do checklist
            <select
              v-model="checklistItemKey"
              class="h-10 rounded-lais border border-lais-border px-3 text-[14px] font-normal text-lais-text"
            >
              <option
                v-for="item in checklistItems"
                :key="item.item_key"
                :value="item.item_key"
              >
                {{ item.item_key }}
              </option>
            </select>
          </label>
          <div class="flex justify-end">
            <LaisPrimaryButton
              icon="i-lucide-file-check-2"
              :is-loading="saving"
              @click="addEvidence"
            >
              Registrar evidência
            </LaisPrimaryButton>
          </div>
        </div>

        <div
          class="grid gap-3 rounded-lais border border-lais-border-soft px-4 py-4"
        >
          <p class="text-[13px] font-semibold text-lais-text">
            Próximo blocker: {{ firstOpenItem?.item_key || 'nenhum' }}
          </p>
          <label
            class="grid gap-1 text-[12px] font-semibold text-lais-text-muted"
          >
            Motivo
            <textarea
              v-model="reviewReason"
              rows="2"
              class="rounded-lais border border-lais-border px-3 py-2 text-[14px] font-normal text-lais-text"
            />
          </label>
          <div class="flex flex-wrap justify-end gap-2">
            <LaisPrimaryButton
              variant="ghost"
              icon="i-lucide-badge-check"
              :disabled="!firstOpenItem"
              :is-loading="saving"
              @click="reviewOpenItem('approved')"
            >
              Aprovar item
            </LaisPrimaryButton>
            <LaisPrimaryButton
              variant="ghost"
              icon="i-lucide-shield-alert"
              :disabled="!firstOpenItem"
              :is-loading="saving"
              @click="reviewOpenItem('waived')"
            >
              Waiver
            </LaisPrimaryButton>
          </div>
        </div>
      </template>
    </div>
  </LaisCard>
</template>
